import type { HtmlPrompt } from '../../types/prompts.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { type AnimationConfig, AnimationStage } from './animation-types.ts'
import { AbstractAnimationCoordinator } from './abstract-animation-coordinator.ts'

/**
 * @internal
 */
export class CssAnimationCoordinator extends AbstractAnimationCoordinator {
    protected lastConfig: AnimationConfig | (() => AnimationConfig) | undefined

    public constructor(prompt: HtmlPrompt, logger?: Logger) {
        super(prompt, logger ?? TaggedLogger._getInstance('CSSAnimationCoordinator'))
    }

    protected _clearAnimationClassNames(element: HTMLElement, animationId?: string) {
        const clsPrefixRgx = new RegExp(`^${AbstractAnimationCoordinator.ANIMATION_PREFIX}_`)
        Array.from(element.classList).forEach((cls) => {
            if (cls !== animationId && clsPrefixRgx.test(cls)) {
                element.classList.remove(cls)
            }
        })
    }

    protected _handleOrientationChange(nextOrientation: string, prevOrientation: string) {
        if (!this._promptElement || !this.lastConfig || nextOrientation === prevOrientation) {
            return
        }

        this._clearAnimationClassNames(this._promptElement)
    }

    protected _applyAnimation(
        stage: AnimationStage,
        config: AnimationConfig | (() => AnimationConfig),
        onComplete?: () => void,
    ): void {
        const promptElement = this._promptElement
        if (!promptElement) {
            return
        }

        if (typeof config === 'function') {
            config = config()
        }
        this.lastConfig = config

        config ??= this._exitAnimationConfig
        if (!config.enabled) {
            this.logger.debug(`${stage} animation disabled for Prompt ${this.prompt.id}`)
            return
        }

        const animationId = this._generateAnimationId()
        const keyframes = this._getAnimationSteps(stage, config, promptElement.getBoundingClientRect())

        const styles = this._document.createElement('style')
        styles.id = `${animationId}_style`
        styles.setAttribute(`data-${AbstractAnimationCoordinator.ANIMATION_PREFIX}`, 'true')

        styles.innerText = [
            [
                `@keyframes ${animationId} {`,
                `from { opacity: ${keyframes.from.opacity}; transform: ${keyframes.from.transform} }`,
                `to { opacity: ${keyframes.to.opacity}; transform: ${keyframes.to.transform} }`,
                '}',
            ].join(' '),
            [
                `.push-sdk-prompt-container.${animationId} {`,
                `animation-name: ${animationId};`,
                `animation-duration: ${config.duration_milliseconds / 1000}s;`,
                'animation-fill-mode: forwards;',
                '}',
            ].join(' '),
        ].join(' ')

        this._promptPackageElement!.prepend(styles)
        promptElement.classList.add(animationId)

        this._clearAnimationClassNames(promptElement, animationId)

        const styleBlocks = Array.from(this._promptPackageElement!.getElementsByTagName('style'))
        for (const styleBlock of styleBlocks) {
            if (styleBlock.id === styles.id || !styleBlock.dataset[AbstractAnimationCoordinator.ANIMATION_PREFIX]) {
                continue
            }
            styleBlock.remove()
        }

        promptElement.addEventListener('animationend', () => {
            try {
                onComplete?.()
            } catch {}
        })
    }
}
